import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { createApiUrl, createApiUrlWithPrefix } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Plus,
  Search,
  CheckCircle,
  XCircle,
  User,
  CreditCard,
  Receipt,
  Eye,
  Trash2,
  Shield,
  Calendar,
  BookOpen,
  Gift,
  RefreshCw,
  X
} from 'lucide-react';

const OwnerPayments = () => {
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useAuth();
  const { getSettingAsNumber } = useSettings();
  const isSuperAdmin = currentUser?.role === 4 || currentUser?.role === 'SuperAdmin';
  const basicPrice = getSettingAsNumber('owner.subscription.basic', 40000);
  const mediumPrice = getSettingAsNumber('owner.subscription.medium', 60000);
  const luxuryPrice = getSettingAsNumber('owner.subscription.luxury', 80000);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoricalPaymentModal, setShowHistoricalPaymentModal] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerPayments, setOwnerPayments] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState('all');
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [ownerForfaits, setOwnerForfaits] = useState([]);
  const [ownerHorses, setOwnerHorses] = useState([]);
  const [showHorseStatusModal, setShowHorseStatusModal] = useState(false);
  const [loadingHorseStatus, setLoadingHorseStatus] = useState(false);
  const [horseStatusData, setHorseStatusData] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    subscriptionTier: 1,
    description: '',
    selectedMonths: [], // Array of {year, month} objects
    horseId: null // Selected horse ID
  });

  // Historical payment form state (separate from normal payment)
  const [historicalPaymentForm, setHistoricalPaymentForm] = useState({
    amount: '',
    subscriptionTier: 1,
    description: '',
    selectedMonths: [],
    paymentDate: new Date().toISOString().split('T')[0]
  });

    const [refreshKey, setRefreshKey] = useState(0);
const subscriptionTypes = useMemo(() => {
    return [
      { 
        value: 1, 
        label: t('ownerPayments.subscriptionTiers.basic'), 
        icon: Shield, 
        color: 'bg-blue-100 text-blue-800',
        description: t('ownerPayments.subscriptionTiers.basic') + ' - ' + t('ownerPayments.subscriptionTiers.basic'),
        defaultAmount: basicPrice,
        enabled: true
      },
      { 
        value: 2, 
        label: t('ownerPayments.subscriptionTiers.premium'), 
        icon: Calendar, 
        color: 'bg-green-100 text-green-800',
        description: t('ownerPayments.subscriptionTiers.premium') + ' - ' + t('ownerPayments.subscriptionTiers.premium'),
        defaultAmount: mediumPrice,
        enabled: true
      },
      {
        value: 3, 
        label: t('ownerPayments.subscriptionTiers.vip'), 
        icon: Gift, 
        color: 'bg-purple-100 text-purple-800',
        description: t('ownerPayments.subscriptionTiers.vip') + ' - ' + t('ownerPayments.subscriptionTiers.vip'),
        defaultAmount: luxuryPrice,
        enabled: true
      }
    ];
  }, [t, basicPrice, mediumPrice, luxuryPrice]);

  // Set initial payment form values
  useEffect(() => {
    const initialSubscriptionType = subscriptionTypes.find(type => type.value === paymentForm.subscriptionTier);
    if (initialSubscriptionType?.defaultAmount !== undefined) {
      setPaymentForm(prev => ({
        ...prev,
        amount: initialSubscriptionType.defaultAmount.toString(),
        description: initialSubscriptionType.label
      }));
    }
  }, [paymentForm.subscriptionTier, subscriptionTypes]);

  const fetchOwners = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: 'Owner',
        _t: Date.now(),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(`${API_BASE_URL}/api/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Debug logging
      console.log('API Response:', response.data);
      console.log('Response structure:', {
        hasUsers: 'users' in response.data,
        hasData: 'data' in response,
        dataType: typeof response.data,
        dataKeys: Object.keys(response.data || {})
      });
      
      // Check if response has users data
      const owners = response.data?.users || response.data || [];
      
      if (!Array.isArray(owners)) {
        console.error('Invalid response structure:', response.data);
        setOwners([]);
        setTotalPages(0);
        return;
      }
      
      // Set default subscription status for each owner (no API calls needed)
      const ownersWithStatus = owners.map(owner => ({
        ...owner,
        subscriptionStatus: { HasActiveSubscription: false, Subscription: null }
      }));
      
      setOwners(ownersWithStatus);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error(t('common.failedToFetchOwners'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  const fetchOwnerPayments = useCallback(async (ownerId, paymentType = 'all') => {
    try {
      let payments = [];
      
      if (ownerId) {
        // Fetch payments for specific owner
        const response = await axios.get(createApiUrlWithPrefix(`OwnerPayment/owner/${ownerId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
        payments = response.data || [];
        
        // Filter by payment type if not 'all'
        if (paymentType !== 'all') {
          payments = payments.filter(payment => payment.subscriptionTier === paymentType);
        }
      } else {
        // Fetch all payments for admin view
        const response = await axios.get(`${API_BASE_URL}/api/OwnerPayment/all`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        payments = response.data || [];
      }
      
      // Sort by payment date (newest first)
      // Sort payments: non-historical first, then historical, both sorted by date (newest first)
      payments.sort((a, b) => {
        // First, separate historical from non-historical
        if (a.isHistorical !== b.isHistorical) {
          return a.isHistorical ? 1 : -1; // Non-historical first (false comes before true)
        }
        // If both are same type, sort by date (newest first)
        return new Date(b.paymentDate) - new Date(a.paymentDate);
      });
      setOwnerPayments(payments);
    } catch (error) {
      console.error('Error fetching owner payments from API:', error);
      toast.error('Failed to fetch payment data from API.');
      setOwnerPayments([]);
    }
  }, []);

  const handleCheckHorses = async (owner) => {
    try {
      setSelectedOwner(owner);
      setLoadingHorseStatus(true);
      setShowHorseStatusModal(true);
      
      const [forfaits, horses] = await Promise.all([
        axios.get(createApiUrlWithPrefix(`OwnerPayment/forfaits/${owner.id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(createApiUrlWithPrefix(`horses?ownerId=${owner.id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const forfaitsData = forfaits.data || [];
      const horsesData = horses.data?.horses || horses.data || [];
      
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      const processedStatus = horsesData.map(horse => {
        const horseForfaits = forfaitsData.filter(f => f.horseId === horse.id);
        const isCurrentMonthPaid = horseForfaits.some(f => f.year === currentYear && f.month === currentMonth);
        const paidMonthsCount = horseForfaits.length;
        const currentForfait = horseForfaits.sort((a, b) => (b.year * 100 + b.month) - (a.year * 100 + a.month))[0];

        return {
          ...horse,
          isCurrentMonthPaid,
          paidMonthsCount,
          currentForfait
        };
      });

      setHorseStatusData(processedStatus);
    } catch (error) {
      console.error('Error fetching horse status:', error);
      toast.error('Failed to fetch horse status data.');
    } finally {
      setLoadingHorseStatus(false);
    }
  };

  const fetchOwnerForfaits = useCallback(async (ownerId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`OwnerPayment/forfaits/${ownerId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setOwnerForfaits(response.data || []);
    } catch (error) {
      console.error('Error fetching owner forfaits:', error);
      toast.error(t('common.failedToFetchSubscriptionForfaits'));
    }
  }, []);

  const fetchOwnerHorses = useCallback(async (ownerId) => {
    try {
      console.log(`Fetching horses for owner ID: ${ownerId}`);
      const response = await axios.get(createApiUrlWithPrefix(`horses?ownerId=${ownerId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      console.log(`Horses fetched for owner ${ownerId}:`, response.data);
      return response.data?.horses || response.data || [];
    } catch (error) {
      console.error('Error fetching owner horses:', error);
      console.error('Error response:', error.response?.data);
      toast.error(t('common.failedToFetchHorsesForOwner'));
      return [];
    }
  }, []);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleSubscriptionTypeChange = (type) => {
    const typeValue = parseInt(type);
    const subscriptionType = subscriptionTypes.find(st => st.value === typeValue);
    console.log('Subscription type changed:', { type, typeValue, subscriptionType });
    
    setPaymentForm(prev => {
      let amount = subscriptionType?.defaultAmount || 0;
      let selectedMonths = prev.selectedMonths;
      
      // If no months selected, default to current month
      if (selectedMonths.length === 0) {
        const currentDate = new Date();
        selectedMonths = [{
          year: currentDate.getFullYear(),
          month: currentDate.getMonth() + 1
        }];
        amount = subscriptionType?.defaultAmount || 0;
      }
      
      return {
        ...prev,
        subscriptionTier: typeValue,
        amount: amount.toString(),
        description: subscriptionType?.label || '',
        selectedMonths: selectedMonths
      };
    });
  };

  const toggleMonthSelection = (year, month) => {
    setPaymentForm(prev => {
      const isSelected = prev.selectedMonths.some(m => m.year === year && m.month === month);
      
      let newSelectedMonths;
      if (isSelected) {
        // Remove month
        newSelectedMonths = prev.selectedMonths.filter(m => !(m.year === year && m.month === month));
      } else {
        // Add month
        newSelectedMonths = [...prev.selectedMonths, { year, month }];
      }
      
      // Update amount based on selected months and subscription tier
      const subscriptionType = subscriptionTypes.find(st => st.value === prev.subscriptionTier);
      const newAmount = newSelectedMonths.length * (subscriptionType?.defaultAmount || 0);
      
      return {
        ...prev,
        selectedMonths: newSelectedMonths,
        amount: newAmount.toString()
      };
    });
  };

  const isMonthSelected = (year, month) => {
    return paymentForm.selectedMonths.some(m => m.year === year && m.month === month);
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    
    if (!selectedOwner) {
      toast.error(t('common.pleaseSelectOwner'));
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t('common.pleaseLoginToCreatePayments'));
      return;
    }

    // Validate form data
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error(t('common.pleaseEnterValidAmount'));
      return;
    }

    if (!paymentForm.description.trim()) {
      toast.error(t('common.pleaseEnterDescription'));
      return;
    }

    if (!paymentForm.horseId) {
      toast.error('Please select a horse for this subscription');
      return;
    }

    try {
      // Get current user ID with better error handling
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        toast.error('User session expired. Please login again.');
        return;
      }

      const paymentData = {
        ownerId: selectedOwner.id,
        amount: parseFloat(paymentForm.amount),
        subscriptionTier: parseInt(paymentForm.subscriptionTier),
        description: paymentForm.description.trim(),
        processedBy: parseInt(currentUserId),
        selectedMonths: paymentForm.selectedMonths.length > 0 ? paymentForm.selectedMonths : null,
        horseId: paymentForm.horseId,
        isHistorical: false
      };

      console.log('Creating payment with data:', paymentData);

      // Create payment using OwnerPayment controller
      const response = await axios.post(`${API_BASE_URL}/api/OwnerPayment/create`, paymentData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Payment created successfully:', response.data);
      toast.success(`${subscriptionTypes.find(st => st.value === paymentForm.subscriptionTier)?.label} payment created successfully`);
      
      // Close modal and reset form
      setShowPaymentModal(false);
      setPaymentForm({
        amount: '',
        subscriptionTier: 1,
        description: '',
        selectedMonths: [],
        horseId: null
      });
      
      // Refresh payment history if open
      if (showPaymentHistory && selectedOwner) {
        await fetchOwnerPayments(selectedOwner.id, selectedPaymentType);
      }
      
      // Refresh owners to update their status
      await fetchOwners();
      
    } catch (apiError) {
      console.error('API error:', apiError);
      console.error('Error response:', apiError.response?.data);
      console.error('Error status:', apiError.response?.status);
      
      // Show specific error messages
      if (apiError.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (apiError.response?.status === 400) {
        const errorMessage = apiError.response?.data?.message || apiError.response?.data || 'Invalid payment data';
        console.error('Validation error:', errorMessage);
        toast.error(errorMessage);
      } else if (apiError.response?.status === 500) {
        const errorMessage = apiError.response?.data?.message || 'Server error. Please try again.';
        toast.error(errorMessage);
      } else {
        toast.error('Failed to create payment. Please check your connection and try again.');
      }
    }
  };

  // Handler for historical payments (separate from normal payments)
  const handleCreateHistoricalPayment = async (e) => {
    e.preventDefault();
    
    if (!selectedOwner) {
      toast.error(t('common.pleaseSelectOwner'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t('common.pleaseLoginToCreatePayments'));
      return;
    }

    if (!historicalPaymentForm.amount || parseFloat(historicalPaymentForm.amount) <= 0) {
      toast.error(t('common.pleaseEnterValidAmount'));
      return;
    }

    // Description is optional for historical payments

    try {
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        toast.error('User session expired. Please login again.');
        return;
      }

      const paymentData = {
        ownerId: selectedOwner.id,
        subscriptionTier: parseInt(historicalPaymentForm.subscriptionTier),
        amount: parseFloat(historicalPaymentForm.amount),
        description: historicalPaymentForm.description?.trim() || '',
        processedBy: parseInt(currentUserId),
        isHistorical: true, // Mark as historical
        paymentDate: historicalPaymentForm.paymentDate // Use custom date
      };

      await axios.post(`${API_BASE_URL}/api/OwnerPayment/create`, paymentData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Historical payment added successfully');
      
      // Close modal and reset form
      setShowHistoricalPaymentModal(false);
      setHistoricalPaymentForm({
        amount: '',
        subscriptionTier: 1,
        description: '',
        selectedMonths: [],
        paymentDate: new Date().toISOString().split('T')[0]
      });
      
      // Refresh owners to update their status
      await fetchOwners();
    } catch (error) {
      console.error('Error creating historical payment:', error);
      toast.error(error.response?.data?.message || 'Failed to create historical payment');
    }
  };

  const handleCancelPayment = async (paymentId) => {
    try {
      // Cancel payment using OwnerPayment controller
      await axios.post(createApiUrlWithPrefix(`OwnerPayment/cancel/${paymentId}`), {}, {
          headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Payment cancelled successfully');
      
      // Refresh data
      await fetchOwners();
      if (showPaymentHistory && selectedOwner) {
        await fetchOwnerPayments(selectedOwner.id, selectedPaymentType);
      }
    } catch (apiError) {
      console.error('API error:', apiError);
      
      // Show error message
      if (apiError.response?.status === 404) {
        toast.error('Payment endpoints not available. Please check API configuration.');
      } else if (apiError.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else {
        toast.error('Failed to cancel payment. Please try again.');
      }
    }
  };

  const openPaymentModal = async (owner) => {
    setSelectedOwner(owner);
    
    console.log('Opening payment modal for owner:', owner);
    
    // Fetch horses for this owner
    const horses = await fetchOwnerHorses(owner.id);
    setOwnerHorses(horses);
    
    // Check if owner has horses
    if (horses.length === 0) {
      toast.error('This owner has no horses. Please add horses before creating a subscription.');
      return;
    }
    
    // Reset payment form to default values
    const defaultSubscriptionType = subscriptionTypes[0]; // First type (Basic)
    console.log('Opening payment modal:', { 
      owner, 
      horses,
      defaultSubscriptionType, 
      subscriptionTypes: subscriptionTypes.map(st => ({ 
        value: st.value, 
        label: st.label, 
        enabled: st.enabled 
      })),
      token: localStorage.getItem('token') ? 'Present' : 'Missing'
    });
    
    // Default to current month
    const currentDate = new Date();
    const defaultSelectedMonths = [{
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1
    }];
    
    setPaymentForm({
      amount: defaultSubscriptionType.defaultAmount.toString(),
      subscriptionTier: defaultSubscriptionType.value,
      description: defaultSubscriptionType.label,
      selectedMonths: defaultSelectedMonths,
      horseId: horses.length > 0 ? horses[0].id : null // Default to first horse if available
    });
    setShowPaymentModal(true);
  };

  const openPaymentHistory = async (owner) => {
    setSelectedOwner(owner);
    setShowPaymentHistory(true);
    await Promise.all([
      fetchOwnerPayments(owner.id, selectedPaymentType),
      fetchOwnerForfaits(owner.id)
    ]);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const paginatedOwners = owners;

  const getStatusIcon = (status) => {
    if (status) return <CheckCircle className="w-4 h-4 text-green-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getStatusText = (status) => {
    return status ? t('common.active') : t('common.inactive');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  } 
*/

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 px-4 sm:px-6 lg:px-8 py-6" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} key={refreshKey}>
        {/* Fixed Header Section */}
        <div className="">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('ownerPayments.title')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">{t('ownerPayments.subtitle')}</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder={t('ownerPayments.searchPlaceholder')}
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={fetchOwners}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('common.refresh')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Section */}
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedOwners.map((owner) => (
          <div key={owner.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                  </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                  {owner.firstName} {owner.lastName}
                </h3>
                  <p className="text-sm text-gray-500">{owner.email}</p>
                    </div>
                    </div>
                  </div>
                  
            {/* Horse Count Card */}
            <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-100">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-green-700">{owner.horseCount || 0}</span>
                </div>
                <span className="text-xs font-bold text-green-800">
                  {i18n.language === 'ar' ? 'عدد الخيول' : 'Number of Horses'}
                </span>
              </div>
              <p className="text-[10px] text-green-600 font-medium uppercase tracking-wider">
                {owner.horseCount === 1 
                  ? (i18n.language === 'ar' ? 'حصان واحد مسجل' : '1 Horse registered')
                  : (i18n.language === 'ar' ? `${owner.horseCount || 0} خيول مسجلة` : `${owner.horseCount || 0} Horses registered`)
                }
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={() => openPaymentModal(owner)}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t('ownerPayments.actions.addPayment')}
                </button>
                <button
                  onClick={() => handleCheckHorses(owner)}
                  className="flex-1 bg-white text-blue-600 border border-blue-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {i18n.language === 'ar' ? 'فحص الخيول' : 'Check Horses'}
                </button>
              </div>
              <button
                onClick={() => openPaymentHistory(owner)}
                className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2 transition-colors"
              >
                <Eye className="w-4 h-4" />
                {t('common.history')}
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => {
                    setSelectedOwner(owner);
                    setShowHistoricalPaymentModal(true);
                  }}
                  className="w-full bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg text-sm font-medium hover:bg-yellow-200 flex items-center justify-center gap-2 border border-yellow-300 transition-colors"
                >
                  <Receipt className="w-4 h-4" />
                  Add Historical Payment
                </button>
              )}
            </div>
          </div>
        ))}
          </div>
        </div>

        {/* Pagination - Sticky at bottom */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-bold text-blue-600">{totalItems}</span> results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.previous')}
              </button>
              
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs sm:max-w-sm md:max-w-md max-h-[96vh] overflow-hidden flex flex-col mx-1">
            <div className="p-2 sm:p-3 flex-shrink-0">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">
                Add Payment - {selectedOwner.firstName} {selectedOwner.lastName}
              </h2>
              
              <div className="overflow-y-auto flex-1 pr-2">
                <form id="payment-form" onSubmit={handleCreatePayment} className="space-y-2">
                          <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {t('common.subscriptionType')}
                  </label>
                  <div className="grid grid-cols-1 gap-2 mb-3">
                    {subscriptionTypes.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleSubscriptionTypeChange(type.value.toString())}
                        disabled={!type.enabled}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-xl border-2 transition-all duration-200 ${
                          parseInt(paymentForm.subscriptionTier) === type.value
                            ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-md transform scale-[1.02]'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        } ${!type.enabled ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-lg ${parseInt(paymentForm.subscriptionTier) === type.value ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                            {React.createElement(type.icon, { className: "w-4 h-4" })}
                          </div>
                          <span className="text-sm font-bold">{type.label}</span>
                        </div>
                        <span className={`text-xs font-black px-2.5 py-1 rounded-full border ${
                          parseInt(paymentForm.subscriptionTier) === type.value 
                            ? 'bg-blue-100 border-blue-200 text-blue-700' 
                            : 'bg-gray-50 border-gray-100 text-gray-500'
                        }`}>
                          {type.defaultAmount.toLocaleString()} DA
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                 {/* Horse Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {t('common.selectHorseForSubscription')}
                  </label>
                  {ownerHorses.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {ownerHorses.map(horse => (
                        <button
                          key={horse.id}
                          type="button"
                          onClick={() => setPaymentForm(prev => ({ ...prev, horseId: horse.id }))}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all duration-200 ${
                            paymentForm.horseId === horse.id
                              ? 'border-green-500 bg-green-50 text-green-800 shadow-md transform scale-[1.02]'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-lg">🐎</span>
                          <span className="text-sm font-bold">{horse.name}</span>
                          {paymentForm.horseId === horse.id && (
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 ml-1 shadow-sm animate-pulse"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="w-full px-3 py-3 border-2 border-red-200 border-dashed rounded-xl bg-red-50 text-red-600 text-xs text-center font-bold">
                      ⚠️ No horses found for this owner.
                    </div>
                  )}
                  {ownerHorses.length === 0 && (
                    <p className="text-[10px] text-red-500 mt-2 font-bold bg-white border border-red-100 px-2 py-1 rounded-lg inline-block">
                      Please add horses first to enable subscription payments.
                    </p>
                  )}
                </div>

                {/* Month Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
{t('common.selectMonthsForSubscription')}
                  </label>
                  <div className="border border-gray-300 rounded-lg p-1 max-h-24 sm:max-h-32 overflow-y-auto">
                    <div className="space-y-1">
                      {/* Current Year */}
                      <div>
                        <h4 className="font-medium text-gray-800 mb-0.5 text-xs">{new Date().getFullYear()}</h4>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-0.5">
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                            const isSelected = isMonthSelected(new Date().getFullYear(), month);
                            const isPastMonth = month < new Date().getMonth() + 1;
                            const isCurrentMonth = month === new Date().getMonth() + 1;
                            
                            return (
                <button
                                key={month}
                                type="button"
                                onClick={() => !isPastMonth && toggleMonthSelection(new Date().getFullYear(), month)}
                                disabled={isPastMonth}
                                className={`p-0.5 sm:p-1 text-xs rounded border transition-all ${
                                  isSelected 
                                    ? 'bg-green-100 border-green-500 text-green-800' 
                                    : isPastMonth
                                    ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                    : isCurrentMonth
                                    ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'short' })}
                                {isCurrentMonth && '*'}
                </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Next Year */}
                      <div>
                        <h4 className="font-medium text-gray-800 mb-0.5 text-xs">{new Date().getFullYear() + 1}</h4>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-0.5">
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                            const isSelected = isMonthSelected(new Date().getFullYear() + 1, month);
                            
                            return (
                <button
                                key={month}
                                type="button"
                                onClick={() => toggleMonthSelection(new Date().getFullYear() + 1, month)}
                                className={`p-0.5 sm:p-1 text-xs rounded border transition-all ${
                                  isSelected 
                                    ? 'bg-green-100 border-green-500 text-green-800' 
                                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'short' })}
                </button>
                            );
                          })}
              </div>
            </div>
          </div>
        </div>

                  {paymentForm.selectedMonths.length > 0 && (
            <div className="mt-2 sm:mt-3">
<p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">{t('common.selectedMonths')}:</p>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {paymentForm.selectedMonths.map((month, index) => (
                          <span
                            key={index}
                            className="px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center gap-1"
                          >
                            {new Date(2024, month.month - 1).toLocaleDateString('en-US', { month: 'long' })} {month.year}
                <button
                              type="button"
                              onClick={() => toggleMonthSelection(month.year, month.month)}
                              className="text-green-600 hover:text-green-800"
                >
                              ×
                </button>
                          </span>
                        ))}
              </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-green-600 mt-2">
💡 {t('common.selectSpecificMonthsForSubscription')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
{t('common.amount')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={(() => {
                      const subscriptionType = subscriptionTypes.find(st => st.value === paymentForm.subscriptionTier);
                      return subscriptionType?.defaultAmount?.toString() || '';
                    })()}
                    required
                  />
                  {(() => {
                    const subscriptionType = subscriptionTypes.find(st => st.value === paymentForm.subscriptionTier);
                    if (subscriptionType?.defaultAmount !== undefined) {
                      return (
                        <p className="text-xs text-blue-600 mt-1">
💡 {subscriptionType.defaultAmount.toLocaleString()} DA × {paymentForm.selectedMonths.length} {t('common.months')} = {paymentForm.amount} DA
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>

                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
{t('common.description')}
                    </label>
                    <input
                    type="text"
                    value={paymentForm.description}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                  />
                </div>


                </form>
              </div>
              
              {/* Fixed Footer with Buttons */}
              <div className="p-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-1.5">
                  <button
                    type="submit"
                    form="payment-form"
                    className="flex-1 bg-blue-600 text-white py-1.5 px-3 rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
{t('common.createPayment')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-1.5 px-3 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
{t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
                  </div>
                )}

      {/* Payment History Modal */}
      {showPaymentHistory && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
{t('common.paymentHistory')} - {selectedOwner.firstName} {selectedOwner.lastName}
                </h2>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedPaymentType}
                    onChange={(e) => {
                      setSelectedPaymentType(e.target.value);
                      fetchOwnerPayments(selectedOwner.id, e.target.value);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">{t('common.allTypes')}</option>
                    {subscriptionTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowPaymentHistory(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
                </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {ownerPayments.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">{t('common.noPaymentsFound')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ownerPayments.map((payment) => {
                    const subscriptionType = subscriptionTypes.find(st => st.value === payment.subscriptionTier);
                    const IconComponent = subscriptionType?.icon || CreditCard;
                    
                    return (
                      <div key={payment.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${subscriptionType?.color || 'bg-gray-100'}`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-gray-900">{subscriptionType?.label || payment.subscriptionTier}</h3>
                                {payment.isHistorical && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                    Historical
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{payment.description}</p>
                              {payment.horseId && (
                                <p className="text-sm text-blue-600 flex items-center gap-1">
                                  🐎 Horse ID: {payment.horseId}
                                </p>
                              )}
                              <p className="text-xs text-gray-400">
                                {formatDate(payment.paymentDate)} • {payment.processedByUserName || 'System'}
                              </p>
                            </div>
                </div>

                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                              <div className="flex items-center gap-1">
                                {payment.isCancelled ? (
                                  <>
                                    <XCircle className="w-4 h-4 text-red-500" />
                                    <span className="text-sm text-red-600">{t('common.cancelled')}</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span className="text-sm text-green-600">{t('common.active')}</span>
                                  </>
                                )}
                              </div>
                </div>

                            {!payment.isCancelled && (
                  <button
                                onClick={() => handleCancelPayment(payment.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Cancel Payment"
                              >
                                <Trash2 className="w-4 h-4" />
                  </button>
                            )}
                </div>
            </div>
          </div>
                    );
                  })}
        </div>
      )}
    </div>
            
            {/* Owner Forfaits Section */}
            <div className="border-t border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Subscription Forfaits
              </h3>
              
              {ownerForfaits.length === 0 ? (
                <div className="text-center py-6">
                  <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">{t('common.noSubscriptionForfaitsFound')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ownerForfaits.map((forfait) => {
                    const subscriptionType = subscriptionTypes.find(st => st.value === forfait.tier);
                    const isCurrentMonth = forfait.year === new Date().getFullYear() && 
                                         forfait.month === new Date().getMonth() + 1;
                    const isPastMonth = new Date(forfait.year, forfait.month - 1) < new Date();
                    const isFutureMonth = new Date(forfait.year, forfait.month - 1) > new Date();
                    
                    let statusText = '';
                    let statusColor = '';
                    let statusIcon = null;
                    
                    if (forfait.isCancelled) {
                      statusText = 'Cancelled';
                      statusColor = 'text-red-600';
                      statusIcon = <XCircle className="w-4 h-4 text-red-500" />;
                    } else if (isPastMonth) {
                      statusText = 'Expired';
                      statusColor = 'text-gray-500';
                      statusIcon = <XCircle className="w-4 h-4 text-gray-400" />;
                    } else if (isCurrentMonth) {
                      statusText = 'Current';
                      statusColor = 'text-green-600';
                      statusIcon = <CheckCircle className="w-4 h-4 text-green-500" />;
                    } else {
                      statusText = 'Future';
                      statusColor = 'text-blue-600';
                      statusIcon = <Calendar className="w-4 h-4 text-blue-500" />;
                    }
                    
                    return (
                      <div key={forfait.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${subscriptionType?.color || 'bg-gray-100'}`}>
                              <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {subscriptionType?.label || `Tier ${forfait.tier}`} - {new Date(2024, forfait.month - 1).toLocaleDateString('en-US', { month: 'long' })} {forfait.year}
                              </h4>
                              {forfait.horseId && (
                                <p className="text-sm text-blue-600 flex items-center gap-1">
                                  🐎 Horse ID: {forfait.horseId}
                                </p>
                              )}
                              <p className="text-sm text-gray-500">
                                Max Horses: {forfait.maxHorses} • Horse Care Services
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatDate(forfait.startDate)} - {formatDate(forfait.expiryDate)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{formatCurrency(forfait.monthlyAmount)}</p>
                              <div className="flex items-center gap-1">
                                {statusIcon}
                                <span className={`text-sm ${statusColor}`}>{statusText}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Historical Payment Modal */}
      {showHistoricalPaymentModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[98vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Add Historical Payment - {selectedOwner.firstName} {selectedOwner.lastName}
                </h2>
                <button
                  onClick={() => {
                    setShowHistoricalPaymentModal(false);
                    setHistoricalPaymentForm({
                      amount: '',
                      subscriptionTier: 1,
                      description: '',
                      selectedMonths: [],
                      horseId: null,
                      paymentDate: new Date().toISOString().split('T')[0]
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-yellow-700 mt-2 bg-yellow-50 p-2 rounded border border-yellow-200">
                ⚠️ Historical payments are recorded in history only and will NOT affect subscriptions.
              </p>
            </div>
            
            {/* Form */}
            <div className="p-4 overflow-y-auto flex-1">
              <form onSubmit={handleCreateHistoricalPayment}>
                {/* Payment Date */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Date *
                  </label>
                  <input
                    type="date"
                    value={historicalPaymentForm.paymentDate}
                    onChange={(e) => setHistoricalPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Subscription Tier */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subscription Tier
                  </label>
                  <select
                    value={historicalPaymentForm.subscriptionTier}
                    onChange={(e) => setHistoricalPaymentForm(prev => ({ ...prev, subscriptionTier: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    {subscriptionTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} - {type.defaultAmount.toLocaleString()} DA
                      </option>
                    ))}
                  </select>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (DA) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={historicalPaymentForm.amount}
                    onChange={(e) => setHistoricalPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={historicalPaymentForm.description}
                    onChange={(e) => setHistoricalPaymentForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    rows="3"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowHistoricalPaymentModal(false);
                      setHistoricalPaymentForm({
                        amount: '',
                        subscriptionTier: 1,
                        description: '',
                        selectedMonths: [],
                        paymentDate: new Date().toISOString().split('T')[0]
                      });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    Add Historical Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Horse Status Modal */}
      {showHorseStatusModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-blue-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">🐎</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {i18n.language === 'ar' ? 'خيول' : 'Horses for'} {selectedOwner.firstName} {selectedOwner.lastName}
                  </h3>
                  <p className="text-xs text-blue-600 font-medium">
                    {i18n.language === 'ar' ? 'فحص حالة الاشتراك لكل حصان' : 'Check subscription status for each horse'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowHorseStatusModal(false)}
                className="p-2 hover:bg-blue-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="overflow-y-auto p-4 space-y-4">
              {loadingHorseStatus ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-3">
                  <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="text-sm text-gray-500 font-medium">
                    {i18n.language === 'ar' ? 'جاري تحميل البيانات...' : 'Loading horse data...'}
                  </p>
                </div>
              ) : horseStatusData.length > 0 ? (
                horseStatusData.map(horse => (
                  <div key={horse.id} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-center text-2xl">
                          🐎
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">{horse.name}</h4>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{horse.breed || 'Unknown Breed'}</span>
                        </div>
                      </div>
                      
                      <div className={`px-3 py-1.5 rounded-full text-xs font-black flex items-center gap-2 border-2 ${
                        horse.isCurrentMonthPaid 
                          ? 'bg-green-50 border-green-200 text-green-700' 
                          : 'bg-red-50 border-red-200 text-red-700'
                      }`}>
                        {horse.isCurrentMonthPaid ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                        {horse.isCurrentMonthPaid 
                          ? (i18n.language === 'ar' ? 'الشهر الحالي مدفوع' : 'Current Month Paid') 
                          : (i18n.language === 'ar' ? 'الشهر الحالي غير مدفوع' : 'Current Month UNPAID')
                        }
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4 bg-gray-50 p-3 rounded-lg">
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">
                          {i18n.language === 'ar' ? 'الاشتراك الحالي' : 'Current Forfait'}
                        </span>
                        <span className="text-sm font-bold text-gray-800">
                          {horse.currentForfait 
                            ? (subscriptionTypes.find(t => t.value === horse.currentForfait.tier)?.label || horse.currentForfait.tier)
                            : (i18n.language === 'ar' ? 'لا يوجد' : 'No Active Forfait')
                          }
                        </span>
                      </div>
                      <div className="flex flex-col border-l border-gray-200 pl-4">
                        <span className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">
                          {i18n.language === 'ar' ? 'إجمالي الأشهر المدفوعة' : 'Total Paid Months'}
                        </span>
                        <span className="text-sm font-black text-blue-600">
                          {horse.paidMonthsCount} {i18n.language === 'ar' ? 'أشهر' : 'Months'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">
                    {i18n.language === 'ar' ? 'لم يتم العثور على خيول لهذا المشترك' : 'No horses found for this owner.'}
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowHorseStatusModal(false)}
                className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-colors shadow-md"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OwnerPayments;


















